import SEO from '../../components/common/SEO';
import './TermsOfUse.css';

export default function TermsOfUse() {
  return (
    <div className="legal-page">
      <SEO 
        title="Terms of Use"
        description="Review the terms of use and conditions governing the access and usage of the Ashoka IAS Academy website and services."
      />
      {/* Hero */}
      <section className="legal-hero">
        <div className="container">
          <h1 className="legal-hero__title">Terms of Use</h1>
          <p className="legal-hero__subtitle">Last updated: June 15, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="legal-content">
        <div className="container legal-content__inner">
          
          <div className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to Ashoka IAS Academy. By accessing or using our website, enrolling in our courses, or utilizing any of our services, you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Ashoka IAS Academy. We reserve the right to modify these Terms at any time, and your continued use of our services after such modifications constitutes acceptance of the updated Terms.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Services Provided</h2>
            <p>Ashoka IAS Academy provides:</p>
            <ul>
              <li>UPSC and APSC coaching programs (Foundation, Concept-Building, Test Series)</li>
              <li>Online and offline classes</li>
              <li>Study materials and resources</li>
              <li>Mock tests and answer writing practice</li>
              <li>Mentorship and guidance services</li>
              <li>Educational content through our website and digital platforms</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of our services at any time without prior notice.
            </p>
          </div>

          <div className="legal-section">
            <h2>3. Eligibility and Registration</h2>
            <h3>3.1 Age Requirement</h3>
            <p>
              You must be at least 18 years old to register for our courses. If you are under 18, you may use our services only with the involvement and consent of a parent or legal guardian.
            </p>

            <h3>3.2 Account Registration</h3>
            <p>When registering for our services, you agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Course Enrollment and Fees</h2>
            <h3>4.1 Enrollment</h3>
            <p>
              Course enrollment is subject to availability and acceptance by Ashoka IAS Academy. We reserve the right to refuse enrollment to any applicant at our discretion.
            </p>

            <h3>4.2 Fees and Payment</h3>
            <ul>
              <li>All course fees must be paid in full or according to the agreed payment schedule</li>
              <li>Fees are non-refundable except as specified in our Refund Policy</li>
              <li>We reserve the right to modify course fees with reasonable notice</li>
              <li>Late payment may result in suspension of access to course materials and services</li>
              <li>All payments are processed through secure third-party payment gateways</li>
            </ul>

            <h3>4.3 Refund Policy</h3>
            <p>
              Refund requests must be submitted in writing within 7 days of enrollment. Refunds are subject to a processing fee and are granted at our discretion based on the circumstances. No refunds will be provided after course commencement or after 7 days from enrollment, whichever comes first.
            </p>
          </div>

          <div className="legal-section">
            <h2>5. Intellectual Property Rights</h2>
            <h3>5.1 Our Content</h3>
            <p>
              All content provided through our services, including but not limited to study materials, videos, lectures, notes, test papers, and website content, is the intellectual property of Ashoka IAS Academy and is protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3>5.2 Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Copy, reproduce, distribute, or share our course materials with third parties</li>
              <li>Record, screenshot, or capture our online classes without permission</li>
              <li>Modify, adapt, or create derivative works from our content</li>
              <li>Use our content for commercial purposes</li>
              <li>Remove or alter any copyright, trademark, or proprietary notices</li>
              <li>Use automated systems or software to extract data from our website</li>
            </ul>

            <h3>5.3 License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable license to access and use our course materials solely for your personal educational purposes during your enrollment period.
            </p>
          </div>

          <div className="legal-section">
            <h2>6. User Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Harass, abuse, or harm other students or staff</li>
              <li>Share false or misleading information</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our services or servers</li>
              <li>Use our services for any fraudulent or illegal purpose</li>
              <li>Impersonate any person or entity</li>
              <li>Engage in any activity that could damage our reputation</li>
            </ul>
            <p>
              Violation of these conduct rules may result in immediate termination of your access to our services without refund.
            </p>
          </div>

          <div className="legal-section">
            <h2>7. Attendance and Participation</h2>
            <ul>
              <li>Regular attendance and active participation are expected for optimal learning outcomes</li>
              <li>We are not responsible for missed classes due to your absence</li>
              <li>Recorded sessions (if available) are provided as a courtesy and may not cover all content</li>
              <li>Disruptive behavior in classes may result in removal from the program</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>8. Disclaimers and Limitations</h2>
            <h3>8.1 No Guarantee of Results</h3>
            <p>
              While we strive to provide high-quality education and guidance, we do not guarantee that you will pass any examination or achieve specific results. Success depends on various factors including your effort, dedication, and aptitude.
            </p>

            <h3>8.2 Service Availability</h3>
            <p>
              We do not guarantee uninterrupted or error-free access to our services. Technical issues, maintenance, or circumstances beyond our control may temporarily affect service availability.
            </p>

            <h3>8.3 Third-Party Content</h3>
            <p>
              Our services may contain links to third-party websites or resources. We are not responsible for the content, accuracy, or practices of these external sites.
            </p>
          </div>

          <div className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Ashoka IAS Academy shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or opportunities, arising from your use of our services.
            </p>
            <p>
              Our total liability to you for any claims arising from these Terms or your use of our services shall not exceed the amount you paid to us in the six months preceding the claim.
            </p>
          </div>

          <div className="legal-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Ashoka IAS Academy, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from your violation of these Terms or your use of our services.
            </p>
          </div>

          <div className="legal-section">
            <h2>11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to our services at any time, with or without cause, and with or without notice. Upon termination:
            </p>
            <ul>
              <li>Your right to access course materials will cease immediately</li>
              <li>You must cease all use of our intellectual property</li>
              <li>Provisions regarding intellectual property, disclaimers, and limitations of liability shall survive termination</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>12. Privacy</h2>
            <p>
              Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
          </div>

          <div className="legal-section">
            <h2>13. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of our services shall be subject to the exclusive jurisdiction of the courts in Guwahati, Assam.
            </p>
            <p>
              Before initiating any legal proceedings, you agree to first attempt to resolve any dispute through good faith negotiations with us.
            </p>
          </div>

          <div className="legal-section">
            <h2>14. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </div>

          <div className="legal-section">
            <h2>15. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other policies referenced herein, constitute the entire agreement between you and Ashoka IAS Academy regarding your use of our services.
            </p>
          </div>

          <div className="legal-section">
            <h2>16. Contact Information</h2>
            <p>If you have any questions or concerns about these Terms of Use, please contact us:</p>
            <div className="legal-contact">
              <p><strong>Ashoka IAS Academy</strong></p>
              <p>Email: <a href="mailto:ashokaiasacademy123@gmail.com">ashokaiasacademy123@gmail.com</a></p>
              <p>Phone: +91 88228 23003 | +91 91814 11843</p>
              <p><strong>Guwahati Office:</strong><br />
              1st Floor, Nath Complex, Opp. Aruna Memorial Hospital,<br />
              Rajgarh Road, Bhangagarh, Guwahati – 781005</p>
              <p><strong>Nagaon Office:</strong><br />
              Sankar Mission, Panigaon Chari Ali, Opp. Reliance Trends,<br />
              Nagaon – 782001</p>
            </div>
          </div>

          <div className="legal-section">
            <p style={{ fontStyle: 'italic', color: 'var(--color-text-muted)', marginTop: 'var(--space-6)' }}>
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
